// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import { popularUsIndexes } from '@advisor-app/features/utils/benchmark-formatter';
import { useAppNavigate } from '@advisor-app/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useAppSelector } from '@advisor-app/store/store';
import { selectAssetsByType } from '@shared/data-access/slices';
import { assetTypesMap } from '@shared/data-access/interfaces/redux/assets-type-state-types';
import {
  selectBenchmark,
  selectObjectiveKey,
  setHistoricalWindow,
  updateOptimizationConfig,
} from '@advisor-app/store/slices/parameters';
import { selectTaxHarvestingCash } from '@shared/data-access/slices/portfolio-positions';
import { selectWorkflowAsset } from '@advisor-app/store/slices/workflow';
import { useGetCustomUniverse } from './use-get-custom-universe';
import { useConst } from '@shared/ui-kit/hooks/use-const';
import { environment } from '@shared/environment';
import { useTranslation } from 'react-i18next';

export const useAiBuilder = () => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const assetType = useAppSelector(selectWorkflowAsset);

  const objective = useAppSelector(state =>
    selectObjectiveKey(state, 'ai-builder'),
  );
  const [selectedUniverse] = useAppSelector(state =>
    selectBenchmark(state, 'ai-builder'),
  );

  const max_number_stocks = useConst(() => environment.FEATURES.AI_MAX_STOCKS!);

  const { number_stocks } = useGetCustomUniverse();
  const [investment, setInvestment] = useState(0);

  const allowDirectIndexing = useMemo(
    () =>
      popularUsIndexes.includes(selectedUniverse.ticker) ||
      ['FEZ', 'VGK'].includes(selectedUniverse.ticker),
    [selectedUniverse],
  );

  const assetTypes = useAppSelector(state =>
    selectAssetsByType(
      state,
      assetTypesMap[assetType as keyof typeof assetTypesMap],
    ),
  );

  const taxCash = useAppSelector(selectTaxHarvestingCash);

  const handleInvestmentChange = useCallback((value: number) => {
    setInvestment(value);
  }, []);

  /** Updates the number of stocks based on the objective and investment amount. */
  const updateStocksNumber = useCallback(() => {
    const stocksRanges: Array<[min: number, max: number, stocks: number]> = [
      [0, 9999, 15],
      [10_000, 49_999, 30],
      [50_000, 199_999, 60],
      [200_000, 500_000, 80],
    ];

    const getStocks = (investment: number) => {
      const stocksRange = stocksRanges.find(
        ([min, max]) => investment >= min && investment <= max,
      );
      return stocksRange ? stocksRange[2] : max_number_stocks;
    };

    const isDirectIndexing = objective === 'min_tracking_error';
    const stocks = isDirectIndexing ? getStocks(investment) : max_number_stocks;
    const historicalWindow = isDirectIndexing ? 182 : 365;

    dispatch(
      updateOptimizationConfig(['ai-builder', { number_assets: [1, stocks] }]),
    );
    dispatch(setHistoricalWindow(historicalWindow));
  }, [dispatch, investment, max_number_stocks, objective]);

  const showDisabledRunOptimizationButton = useMemo(() => {
    const assetsWithTickers = ['etf', 'mutual_fund'];
    const isAssetWithTicker = assetsWithTickers.includes(assetType);

    const hasTicker = (assetTypes?.length ?? 0) > 0;

    const isTaxHarvesting = params.workflow === 'tax-harvesting';

    const taxInvestment = isTaxHarvesting && investment < taxCash;

    return (isAssetWithTicker && !hasTicker) || investment < 5000 || taxInvestment;
  }, [assetType, assetTypes?.length, params.workflow, investment, taxCash]);

  const runOptimization = useCallback(
    (workflow: 'stock' | 'etf' | 'mutual-fund') => {
      if (objective === 'min_risk_parity' && number_stocks > max_number_stocks) {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t('ai-builder.oops', { ns: 'ai-builder' }) as string,
          content: t('ai-builder.customize-universe-max-stocks-message', {
            ns: 'ai-builder',
            maxNumberStocks: max_number_stocks,
          }) as string,
          buttons: {
            confirm: {
              show: true,
              text: t('ai-builder.ok', { ns: 'ai-builder' }) as string,
            },
          },
        });
        return;
      }

      navigate(`/ai-builder/${workflow}/positions`);
    },
    [max_number_stocks, navigate, number_stocks, objective, t],
  );

  useEffect(() => {
    updateStocksNumber();
  }, [updateStocksNumber]);

  useEffect(() => {
    if (assetType !== 'stock') {
      dispatch(
        updateOptimizationConfig(['ai-builder', { min_weight: 0, max_weight: 1 }]),
      );
    }
  }, [assetType, dispatch]);

  return {
    runOptimization,
    max_number_stocks,
    showDisabledRunOptimizationButton,
    handleInvestmentChange,
    allowDirectIndexing,
  };
};
